import React, { useCallback, useEffect, useRef, useState } from 'react';

// Deployment panel for the system role: runs the fixed commands in api DeployController and streams their output.
const ACTIONS = [
    { id: 'status', label: 'Semak Status', hint: 'Cabang, commit terkini dan versi alat. Tidak mengubah apa-apa.', tone: 'ghost' },
    { id: 'backup-db', label: 'Backup Pangkalan Data', hint: 'Simpan salinan SQL dalam storage/app/backups.', tone: 'ghost' },
    { id: 'backup-code', label: 'Backup Kod', hint: 'Simpan salinan kod dalam storage/app/backups.', tone: 'ghost' },
    { id: 'git-pull', label: 'Git Pull', hint: 'Tarik kod terkini dari cabang main.', tone: '' },
    { id: 'composer', label: 'Composer Install', hint: 'Pasang keperluan PHP.', tone: '' },
    { id: 'npm-build', label: 'Build Frontend', hint: 'Bina semula paparan pengguna. Ambil beberapa minit.', tone: '' },
    { id: 'migrate', label: 'Migrate Pangkalan Data', hint: 'Jalankan migration yang belum dijalankan.', tone: 'warn' },
    { id: 'all', label: 'Deploy Penuh', hint: 'Backup, git pull, composer, build dan migrate, ikut susunan.', tone: 'warn' },
];

const CONFIRM = {
    'git-pull': 'Tarik kod terkini dari cabang main?',
    composer: 'Jalankan Composer Install?',
    'npm-build': 'Bina semula frontend sekarang? Proses ini mengambil beberapa minit.',
    migrate: 'Jalankan migration pangkalan data?',
    all: 'Jalankan deploy penuh (backup, git pull, composer, build, migrate)?',
};

const DeployPanel = () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem('token');
    const [output, setOutput] = useState('');
    const [running, setRunning] = useState('');
    const [error, setError] = useState('');
    const [finishedAt, setFinishedAt] = useState('');
    const outputRef = useRef(null);

    useEffect(() => {
        if (outputRef.current) {
            outputRef.current.scrollTop = outputRef.current.scrollHeight;
        }
    }, [output]);

    const stream = useCallback(async (label, url) => {
        if (running) return;
        setRunning(label);
        setError('');
        setFinishedAt('');
        setOutput('');
        try {
            const response = await fetch(url, {
                method: url.includes('/run/') ? 'POST' : 'GET',
                headers: { Accept: 'text/plain', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            });
            if (!response.ok) {
                setError(response.status === 403
                    ? 'Hanya peranan system dibenarkan menjalankan tindakan ini.'
                    : `Permintaan gagal (HTTP ${response.status}).`);
                return;
            }
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            // Stream so long commands (build, composer) show progress instead of waiting for the end.
            for (;;) {
                // eslint-disable-next-line no-await-in-loop
                const { done, value } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value, { stream: true });
                setOutput((prev) => prev + chunk);
            }
            setFinishedAt(new Date().toLocaleTimeString('ms-MY'));
        } catch (err) {
            setError('Sambungan terputus semasa menjalankan tindakan.');
        } finally {
            setRunning('');
        }
    }, [running, token]);

    const runAction = (action) => {
        if (CONFIRM[action.id] && !window.confirm(CONFIRM[action.id])) return;
        stream(action.label, `${apiUrl}/system/deploy/run/${action.id}`);
    };

    return (
        <div className="app-section">
            <div className="app-section-header">
                <div>
                    <div className="app-section-eyebrow">SYSTEM SETTING</div>
                    <h2>Deploy</h2>
                    <p>Jalankan langkah deploy pada pelayan ini dan lihat outputnya secara langsung. Untuk peranan system sahaja.</p>
                </div>
            </div>

            <div className="app-card">
                <div className="app-tab-panel">
                    <div className="app-deploy-actions">
                        {ACTIONS.map((action) => (
                            <button
                                key={action.id}
                                type="button"
                                className={`app-button ${action.tone === 'ghost' ? 'app-button-ghost' : ''}`.trim()}
                                onClick={() => runAction(action)}
                                disabled={Boolean(running)}
                                title={action.hint}
                            >
                                {running === action.label ? 'Menjalankan...' : action.label}
                            </button>
                        ))}
                        <button
                            type="button"
                            className="app-button app-button-ghost"
                            onClick={() => stream('Log Sistem', `${apiUrl}/system/deploy/logs?lines=200`)}
                            disabled={Boolean(running)}
                        >
                            Log Sistem
                        </button>
                    </div>

                    <ul className="app-deploy-hints">
                        {ACTIONS.map((action) => <li key={action.id}><strong>{action.label}:</strong> {action.hint}</li>)}
                    </ul>

                    {error && <div className="app-form-error" role="alert">{error}</div>}

                    <div className="app-deploy-output-head">
                        <span>{running ? `Sedang berjalan: ${running}` : (finishedAt ? `Selesai pada ${finishedAt}` : 'Output')}</span>
                        {output && !running && (
                            <button type="button" className="app-link app-link-button" onClick={() => setOutput('')}>Kosongkan</button>
                        )}
                    </div>
                    <pre className="app-deploy-output" ref={outputRef}>
                        {output || 'Pilih satu tindakan di atas. Output akan dipaparkan di sini semasa ia berjalan.'}
                    </pre>

                    <p className="app-muted" style={{ marginTop: '.75rem', fontSize: '.85rem' }}>
                        Tindakan dijalankan pada pelayan yang sedang anda buka, bukan pelayan lain. Buat Backup Pangkalan Data sebelum Migrate atau Deploy Penuh.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default DeployPanel;
